import { remove } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

function getWorkerScreeningSurveyUrl(workerId: string) {
  return `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/workers/${workerId}/screening-surveys`;
}

/**
 * Hard-deletes a worker's Home Health & Hospice screening survey so they can re-take it. Backed by the
 * employees-only DELETE endpoint; the response has no body, hence `responseSchema: z.unknown()`.
 */
export function useDeleteWorkerScreeningSurvey(workerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await remove({
        url: getWorkerScreeningSurveyUrl(workerId),
        responseSchema: z.unknown(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const [url] = query.queryKey;
          return typeof url === "string" && url.includes("/screening-surveys");
        },
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.HOME_HEALTH_DELETE_SCREENING_SURVEY_FAILURE,
      userSuccessMessage: "Screening survey reset. The professional can now re-take it.",
      userErrorMessage: "Failed to reset the screening survey.",
    },
  });
}
